import React, { useState, useEffect, useRef } from 'react';
import { ConfigData, AnalyticsData, AnalyticsFilters } from '../lib/types';
import { fetchAnalytics } from '../lib/api';
import { formatCurrency } from '../lib/utils';

interface AnalyticsTabProps {
  configData: ConfigData | null;
  filterCat: string;
  setFilterCat: (v: string) => void;
  filterDateFrom: string;
  setFilterDateFrom: (v: string) => void;
  filterDateTo: string;
  setFilterDateTo: (v: string) => void;
  locale: string;
  currency: string;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'rgba(226,75,74,0.15)', ordered: 'rgba(55,138,221,0.15)',
  delivered: 'rgba(29,158,117,0.15)', closed: 'var(--surface2)',
};
const STATUS_TEXT: Record<string, string> = {
  pending: '#A32D2D', ordered: '#185FA5', delivered: '#0F6E56', closed: 'var(--text3)',
};
const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', ordered: 'Ordered', delivered: 'Delivered', closed: 'Closed',
};
const CHART_COLORS = ['#16A34A', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1'];

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg, 16px)', padding: 18,
  boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03)',
};
const cardTitle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: 'var(--text)', marginBottom: 14 };

function RingProgress({ value, size = 60, stroke = 8, label, color = '#16A34A' }: {
  value: number; size?: number; stroke?: number; label?: string; color?: string;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 50); return () => clearTimeout(t); }, []);
  const r = (size / 2) - (stroke / 2) - 2;
  const circ = 2 * Math.PI * r;
  const offset = mounted ? circ - (Math.min(value, 100) / 100) * circ : circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.25,0.46,0.45,0.94)' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {label && <span style={{ fontSize: size > 70 ? 18 : 14, fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{label}</span>}
      </div>
    </div>
  );
}

// `value` drives slice proportions; the legend renders `valueLabel` if
// provided, otherwise falls back to `value` as a plain number. Lets spend-mode
// donuts show formatted currency in the legend ("$25,390") without breaking
// the proportion math, while count-mode donuts keep showing the raw number.
function DonutChart({ data, size = 90, strokeWidth = 16 }: {
  data: { name: string; value: number; valueLabel?: React.ReactNode; color: string }[]; size?: number; strokeWidth?: number;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 80); return () => clearTimeout(t); }, []);
  const r = (size / 2) - (strokeWidth / 2) - 2;
  const circ = 2 * Math.PI * r;
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let accumulated = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <div style={{ width: size, height: size, flexShrink: 0 }}>
        <svg viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
          {total === 0 ? (
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--border)" strokeWidth={strokeWidth} />
          ) : data.map((d, i) => {
            const pct = d.value / total;
            const gap = 2;
            const dashLen = mounted ? Math.max(pct * circ - gap, 0) : 0;
            const dashOffset = mounted ? -(accumulated / total) * circ : 0;
            accumulated += d.value;
            return (
              <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={d.color}
                strokeWidth={strokeWidth} strokeDasharray={`${dashLen} ${circ - dashLen}`}
                strokeLinecap="round" strokeDashoffset={dashOffset}
                style={{ transition: `stroke-dasharray 0.9s cubic-bezier(0.25,0.46,0.45,0.94) ${i * 0.12}s, stroke-dashoffset 0.9s cubic-bezier(0.25,0.46,0.45,0.94) ${i * 0.12}s` }} />
            );
          })}
        </svg>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
        {data.map((d, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
            opacity: mounted ? 1 : 0, transform: mounted ? 'translateX(0)' : 'translateX(-8px)',
            transition: `opacity 0.4s ease ${0.3 + i * 0.08}s, transform 0.4s ease ${0.3 + i * 0.08}s` }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: d.color, flexShrink: 0 }} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
            <span style={{ color: 'var(--text3)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>{d.valueLabel ?? d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnimBar({ height, maxH, label, value, isLast, delay = 0 }: {
  height: number; maxH: number; label: string; value: string; isLast: boolean; delay?: number;
}) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 100 + delay); return () => clearTimeout(t); }, [delay]);
  const targetH = maxH > 0 ? Math.max(4, (height / maxH) * 60) : 4;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <div style={{ fontSize: 11, color: isLast ? 'var(--text)' : 'var(--text3)', fontFamily: 'var(--font-mono)',
        fontWeight: isLast ? 600 : 400, minHeight: 14, opacity: mounted ? 1 : 0, transition: 'opacity 0.3s ease' }}>
        {height > 0 ? value : ''}
      </div>
      <div style={{ width: '100%', height: mounted ? targetH : 4, background: 'var(--accent)', borderRadius: 3,
        opacity: isLast ? 1 : 0.25 + (maxH > 0 ? (height / maxH) * 0.5 : 0),
        transition: 'height 0.7s cubic-bezier(0.34,1.56,0.64,1)' }} />
      <div style={{ fontSize: 11, color: isLast ? 'var(--text)' : 'var(--text3)', fontFamily: 'var(--font-mono)', fontWeight: isLast ? 600 : 400 }}>{label}</div>
    </div>
  );
}

function TeamBar({ name, count, max, index }: { name: string; count: number; max: number; index: number }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 150 + index * 60); return () => clearTimeout(t); }, [index]);
  const pct = (count / max) * 100;
  return (
    <div style={{ marginBottom: 10, opacity: mounted ? 1 : 0, transform: mounted ? 'translateY(0)' : 'translateY(6px)',
      transition: `opacity 0.4s ease, transform 0.4s ease` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
        <span style={{ fontWeight: 500 }}>{name}</span>
        <span style={{ color: 'var(--text2)', fontFamily: 'var(--font-mono)' }}>{count} order{count !== 1 ? 's' : ''}</span>
      </div>
      <div style={{ height: 5, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: mounted ? `${pct}%` : '0%', borderRadius: 3, background: 'var(--accent)',
          opacity: 0.4 + (count / max) * 0.6, transition: 'width 0.8s cubic-bezier(0.34,1.56,0.64,1)' }} />
      </div>
    </div>
  );
}

export function AnalyticsTab({
  configData, filterCat, setFilterCat,
  filterDateFrom, setFilterDateFrom, filterDateTo, setFilterDateTo,
  locale, currency,
}: AnalyticsTabProps) {

  const userRole = configData?.profile?.role || 'dept_admin';
  const isOrgAdmin = ['org_admin', 'super_admin'].includes(userRole);
  const [view, setView] = useState<'activity' | 'spend'>(isOrgAdmin ? 'spend' : 'activity');
  const [animKey, setAnimKey] = useState(0);
  const categories = configData?.categories || [];
  const categoryBudgets = configData?.category_budgets || {};

  const [showCatDrop, setShowCatDrop] = useState(false);
  const catDropRef = useRef<HTMLDivElement>(null);

  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    if (!showCatDrop) return;
    const handler = (e: MouseEvent) => {
      if (catDropRef.current && !catDropRef.current.contains(e.target as Node)) setShowCatDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showCatDrop]);

  useEffect(() => {
    setAnalyticsLoading(true);
    const filters: AnalyticsFilters = {
      category: filterCat !== 'all' ? filterCat : undefined,
      date_from: filterDateFrom || undefined,
      date_to: filterDateTo || undefined,
    };
    fetchAnalytics(filters).then(res => {
      if (res.ok && res.data) setAnalyticsData(res.data);
      setAnalyticsLoading(false);
    });
  }, [filterCat, filterDateFrom, filterDateTo]);

  const handleViewChange = (v: 'activity' | 'spend') => { setView(v); setAnimKey(k => k + 1); };

  const sc = analyticsData?.status_counts || {};
  const pendingCount = sc['pending'] || 0;
  const deliveredCount = sc['delivered'] || 0;
  const closedCount = sc['closed'] || 0;
  const totalOrders = Object.values(sc).reduce((s, n) => s + n, 0);
  const completedCount = deliveredCount + closedCount;
  const completionRate = totalOrders > 0 ? Math.round((completedCount / totalOrders) * 100) : 0;

  const teamActivity = analyticsData?.team_activity || [];
  const maxTeam = teamActivity[0]?.count || 1;
  const deptData = analyticsData?.department_totals || [];
  const vendorData = analyticsData?.vendor_breakdown || [];
  const categorySpendData = analyticsData?.category_spend || [];
  const totalSpend = categorySpendData.reduce((s, c) => s + c.total, 0);
  const totalOrderCount = categorySpendData.reduce((s, c) => s + c.count, 0);

  const monthlyTrend = analyticsData?.monthly_trend || [];
  const maxMonthCount = Math.max(...monthlyTrend.map(m => m.count), 1);
  const maxMonthSpend = Math.max(...monthlyTrend.map(m => m.spend), 1);

  let budgetTarget = 0;
  let budgetLabel = 'Budget';
  if (filterCat === 'all') { budgetTarget = Object.values(categoryBudgets).reduce((s, b) => s + (b || 0), 0); }
  else { budgetLabel = filterCat + ' budget'; budgetTarget = categoryBudgets[filterCat] || 0; }
  const budgetPct = budgetTarget > 0 ? Math.round((totalSpend / budgetTarget) * 100) : 0;

  const catOptions = [{ value: 'all', label: 'All categories' }, ...categories.map(c => ({ value: c, label: c }))];

  return (
    <div className="pane active">
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', padding: '8px 12px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ display: 'flex', gap: 2, padding: 3, background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)', flexShrink: 0 }}>
          <button onClick={() => handleViewChange('activity')} style={{ padding: '7px 22px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none', background: view === 'activity' ? 'var(--accent)' : 'transparent', color: view === 'activity' ? 'white' : 'var(--text2)', cursor: 'pointer', fontFamily: 'inherit' }}>Progress</button>
          <button onClick={() => handleViewChange('spend')} style={{ padding: '7px 22px', fontSize: 14, fontWeight: 600, borderRadius: 8, border: 'none', background: view === 'spend' ? 'var(--accent)' : 'transparent', color: view === 'spend' ? 'white' : 'var(--text2)', cursor: 'pointer', fontFamily: 'inherit' }}>Budget</button>
        </div>
        <div style={{ position: 'relative' }} ref={catDropRef}>
          <button onClick={() => setShowCatDrop(v => !v)} className="filter-sel" style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            {filterCat === 'all' ? 'All categories' : filterCat}
            <svg viewBox="0 0 12 12" style={{ width: 10, height: 10, fill: 'var(--text3)' }}><path d="M2 4l4 4 4-4"/></svg>
          </button>
          {showCatDrop && (
            <div style={{ position: 'absolute', left: 0, top: '100%', marginTop: 4, background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, padding: 4, minWidth: 160, boxShadow: '0 12px 32px rgba(0,0,0,0.12)', zIndex: 9999 }}>
              {catOptions.map(opt => (
                <button key={opt.value} onClick={() => { setFilterCat(opt.value); setShowCatDrop(false); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', fontSize: 13, color: filterCat === opt.value ? 'var(--accent)' : 'var(--text)', fontWeight: filterCat === opt.value ? 600 : 400, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' as const, borderRadius: 8, fontFamily: 'inherit' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')} onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                  {filterCat === opt.value
                    ? <svg viewBox="0 0 12 12" style={{ width: 12, height: 12, fill: 'none', stroke: 'var(--accent)', strokeWidth: 2 }}><path d="M2 6l3 3 5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    : <span style={{ width: 12 }} />}
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} title="From" style={{ fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }} />
        <span style={{ fontSize: 10, color: 'var(--text3)' }}>to</span>
        <input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} title="To" style={{ fontSize: 12, padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit' }} />
        {(filterDateFrom || filterDateTo) && (<button onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, fontSize: 11, color: 'var(--text2)', cursor: 'pointer', padding: '4px 10px', fontFamily: 'inherit' }}>Clear</button>)}
      </div>

      {analyticsLoading && (<div style={{ textAlign: 'center', padding: '20px 0', fontSize: 13, color: 'var(--text3)' }}>Loading analytics…</div>)}

      {view === 'activity' && !analyticsLoading && (
        <div key={'activity-' + animKey} style={{ animation: 'fadeSlideIn .35s ease-out' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', marginBottom: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, flex: 1 }}>
              <div className="sc cg">
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}><div className="sl">Total orders</div><div style={{ width:28, height:28, background:'var(--teal-bg)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}><svg viewBox="0 0 16 16" style={{ width:14, height:14, fill:'none', stroke:'var(--teal)', strokeWidth:1.3 }}><rect x="3" y="2" width="10" height="12" rx="2"/><path d="M6 6h4M6 9h2.5"/></svg></div></div>
                <div className="sn">{totalOrders}</div>
              </div>
              <div className="sc cr">
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}><div className="sl">Pending</div><div style={{ width:28, height:28, background:'var(--red-bg)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}><svg viewBox="0 0 16 16" style={{ width:14, height:14 }}><circle cx="8" cy="8" r="6" fill="none" stroke="var(--red)" strokeWidth="1.5"/><path d="M8 5v3.5" stroke="var(--red)" strokeWidth="1.5" strokeLinecap="round"/><circle cx="8" cy="11" r="0.8" fill="var(--red)"/></svg></div></div>
                <div className="sn">{pendingCount}</div>
              </div>
              <div className="sc cn">
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}><div className="sl">Completed</div><div style={{ width:28, height:28, background:'var(--surface2)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}><svg viewBox="0 0 16 16" style={{ width:14, height:14 }}><path d="M4 8.5l2.5 2.5L12 5" fill="none" stroke="var(--text2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></div></div>
                <div className="sn">{completedCount}</div>
              </div>
            </div>
            <div style={{ ...cardStyle, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
              <RingProgress value={completionRate} size={64} stroke={7} label={completionRate + '%'} />
              <div><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Completion</div><div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{completedCount} of {totalOrders} delivered</div></div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div style={cardStyle}>
              <div style={cardTitle}>Order pipeline</div>
              <div style={{ display: 'flex', gap: 2, height: 28, borderRadius: 6, overflow: 'hidden', marginBottom: 10 }}>
                {['pending','ordered','delivered','closed'].map(s => { const c = sc[s] || 0; if (c === 0) return null; return <div key={s} style={{ flex: c, background: STATUS_COLORS[s], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 500, color: STATUS_TEXT[s], minWidth: 20 }}>{c}</div>; })}
              </div>
              <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--text3)', flexWrap: 'wrap' }}>
                {['pending','ordered','delivered','closed'].map(s => (<span key={s} style={{ display: 'flex', alignItems: 'center', gap: 3 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: STATUS_COLORS[s], border: '0.5px solid var(--border)' }} />{STATUS_LABELS[s]}</span>))}
              </div>
            </div>
            <div style={cardStyle}>
              <div style={cardTitle}>Monthly trend</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 80 }}>
                {monthlyTrend.map((m, i) => (<AnimBar key={m.month} height={m.count} maxH={maxMonthCount} label={m.label.split(' ')[0]} value={String(m.count)} isLast={i === monthlyTrend.length - 1} delay={i * 80} />))}
              </div>
            </div>
          </div>

          <div style={{ ...cardStyle, marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', ...cardTitle }}><span>Team activity</span><span style={{ fontWeight: 400, color: 'var(--text3)', fontSize: 11 }}>{(filterDateFrom || filterDateTo) ? 'Filtered range' : 'All time'}</span></div>
            {teamActivity.length === 0 ? (<div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No activity found</div>
            ) : teamActivity.slice(0, 8).map((t, i) => (<TeamBar key={t.created_by} name={t.created_by_name || 'Unknown'} count={t.count} max={maxTeam} index={i} />))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={cardStyle}>
              <div style={cardTitle}>By department</div>
              {deptData.length === 0 ? (<div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No data</div>) : (<DonutChart data={deptData.slice(0, 5).map((d, i) => ({ name: d.department_name, value: d.count, color: CHART_COLORS[i % CHART_COLORS.length] }))} />)}
            </div>
            <div style={cardStyle}>
              <div style={cardTitle}>By vendor</div>
              {vendorData.length === 0 ? (<div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No data</div>) : (<DonutChart data={vendorData.slice(0, 5).map((v, i) => ({ name: v.vendor, value: v.count, color: CHART_COLORS[i % CHART_COLORS.length] }))} />)}
            </div>
          </div>
        </div>
      )}

      {view === 'spend' && !analyticsLoading && (
        <div key={'spend-' + animKey} style={{ animation: 'fadeSlideIn .35s ease-out' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', marginBottom: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, flex: 1 }}>
              <div className="sc cg">
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}><div className="sl">Total spend</div><div style={{ width:28, height:28, background:'var(--teal-bg)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}><svg viewBox="0 0 16 16" style={{ width:14, height:14, fill:'none', stroke:'var(--teal)', strokeWidth:1.5 }}><path d="M8 2v12M5 4.5C5 3.7 6.3 3 8 3s3 .7 3 1.5S9.7 6 8 6 5 6.7 5 7.5 6.3 9 8 9s3 .7 3 1.5S9.7 12 8 12s-3-.7-3-1.5"/></svg></div></div>
                <div className="sn">{formatCurrency(totalSpend, currency, 'rounded')}</div>
              </div>
              <div className="sc cn">
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}><div className="sl">Orders</div><div style={{ width:28, height:28, background:'var(--surface2)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}><svg viewBox="0 0 16 16" style={{ width:14, height:14, fill:'none', stroke:'var(--text2)', strokeWidth:1.3 }}><rect x="3" y="2" width="10" height="12" rx="2"/><path d="M6 6h4M6 9h2.5"/></svg></div></div>
                <div className="sn">{totalOrderCount}</div>
              </div>
              <div className="sc cn">
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}><div className="sl">Avg per order</div><div style={{ width:28, height:28, background:'var(--surface2)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}><svg viewBox="0 0 16 16" style={{ width:14, height:14, fill:'none', stroke:'var(--text2)', strokeWidth:1.3 }}><circle cx="8" cy="8" r="6"/><path d="M8 5v3.5l2.5 1.5"/></svg></div></div>
                <div className="sn">{totalOrderCount > 0 ? formatCurrency(totalSpend / totalOrderCount, currency, 'rounded') : `${currency}0`}</div>
              </div>
            </div>
            {budgetTarget > 0 && (
              <div style={{ ...cardStyle, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
                <RingProgress value={budgetPct} size={64} stroke={7} label={budgetPct + '%'} color={budgetPct >= 100 ? 'var(--red)' : budgetPct >= 80 ? 'var(--amber)' : 'var(--teal)'} />
                <div><div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{budgetLabel}</div><div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{formatCurrency(totalSpend, currency, 'rounded')} of {formatCurrency(budgetTarget, currency, 'rounded')}</div></div>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div style={cardStyle}>
              <div style={cardTitle}>Spend trend</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', height: 80 }}>
                {monthlyTrend.map((m, i) => (<AnimBar key={m.month} height={m.spend} maxH={maxMonthSpend} label={m.label.split(' ')[0]} value={formatCurrency(m.spend, currency, 'rounded')} isLast={i === monthlyTrend.length - 1} delay={i * 80} />))}
              </div>
            </div>
            <div style={cardStyle}>
              <div style={cardTitle}>By vendor</div>
              {vendorData.length === 0 ? (<div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No data</div>) : (<DonutChart data={vendorData.slice(0, 5).map((v, i) => ({ name: v.vendor, value: Math.round(v.total), valueLabel: formatCurrency(v.total, currency, 'rounded'), color: CHART_COLORS[i % CHART_COLORS.length] }))} />)}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={cardStyle}>
              <div style={cardTitle}>By department</div>
              {deptData.length === 0 ? (<div style={{ color: 'var(--text3)', fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No data</div>) : (<DonutChart data={deptData.map((d, i) => ({ name: d.department_name + ' (' + d.count + ' orders)', value: Math.round(d.total), valueLabel: formatCurrency(d.total, currency, 'rounded'), color: CHART_COLORS[i % CHART_COLORS.length] }))} size={100} strokeWidth={18} />)}
            </div>
            {categorySpendData.length > 0 && (
              <div style={cardStyle}>
                <div style={cardTitle}>By category</div>
                <DonutChart data={categorySpendData.map((c, i) => ({ name: c.category + ' (' + c.count + ')', value: Math.round(c.total), valueLabel: formatCurrency(c.total, currency, 'rounded'), color: CHART_COLORS[i % CHART_COLORS.length] }))} size={100} strokeWidth={18} />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
